import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { SectionDragAndDropContainer } from "./SectionDragAndDropContainer";
import { isFunction } from "@cayuse/shared-react.utils.type-checks";
import Button from "@cayuse/shared-react.components.button";
import ButtonGroup from "@cayuse/shared-react.components.button-group";
import Modal from "@cayuse/shared-react.components.modal";
import IconButton from "@cayuse/shared-react.components.icon-button";

import { useDictionary } from "hooks";

import styles from "./SectionSettings.css";

const getSectionListStartState = sections => sections.map((section) => ({ id: section.id, text: section.label }));

const baseDictionary = {
	title: "Manage Sections",
	openModalButton: "Open manage sections modal",
	cancelButton: "Cancel",
	saveButton: "Save",
};

const SectionSettings = ({ sections, onChangeMasterUpdate }) => {
	const dictionary = useDictionary("simplifiedBuilder.components.manageSectionsModal");

	const [ showModal, setShowModal ] = useState(false);
	const [ sectionList, setSectionList ] = useState(() => getSectionListStartState(sections));

	const onOpen = useCallback(() => {
		setSectionList(getSectionListStartState(sections));
		setShowModal(true);
	}, [ sections ]);

	const onSave = useCallback(() => {
		if (isFunction(onChangeMasterUpdate)) {
			onChangeMasterUpdate(sectionList);
		}
		setShowModal(false);
	}, [ onChangeMasterUpdate, sectionList ]);

	const onCancel = useCallback(() => {
		setShowModal(false);
  }, []);

	return (
		<>
			<IconButton
				size="small"
				className={styles.manageSettingsButton}
				aria-label={dictionary.openModalButton || baseDictionary.openModalButton}
				onClick={onOpen}
				tertiary
			>
				<i className="cog icon" />
			</IconButton>
			<Modal
				open={showModal}
				closeOnEscape={true}
				size="one-half"
				centered={true}
				onClose={onCancel}
				className={styles.modal}
				ariaLabel={dictionary.title || baseDictionary.title}
			>
				<Modal.Header className="whiteOnBlue">
					<h2>
						{dictionary.title || baseDictionary.title}
					</h2>
				</Modal.Header>
				<Modal.Content scrolling>
					<DndProvider backend={HTML5Backend}>
						<SectionDragAndDropContainer
							sectionList={sectionList}
							onChangeMasterUpdate={onChangeMasterUpdate}
							internalOnChange={setSectionList}
						/>
					</DndProvider>
				</Modal.Content>
				<Modal.Actions>
					<ButtonGroup>
						<Button secondary onClick={onCancel}>{dictionary.cancelButton || baseDictionary.cancelButton}</Button>
						<Button primary onClick={onSave}>{dictionary.saveButton || baseDictionary.saveButton}</Button>
					</ButtonGroup>
				</Modal.Actions>
			</Modal>
		</>
	);
};

SectionSettings.propTypes = {
	onChangeMasterUpdate: PropTypes.func,
	sections: PropTypes.array
};

export default SectionSettings;
